import { useRouterState } from "@tanstack/react-router";
import { CalendarClock } from "lucide-react";
import { useCallback, useDeferredValue, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { PageHeader } from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { isNosNotConfiguredError, useAvailability } from "@/hooks/use-availability";
import { useSession } from "@/lib/auth-client";
import type { GroupBy } from "@/lib/availability/groups";
import { stepWindow } from "@/lib/availability/window";
import { canSeeResourcesPage } from "@/lib/nav-access";
import { AvailabilityControls } from "./AvailabilityControls";
import { AvailabilityGrid } from "./AvailabilityGrid";
import { AvailabilityLegend } from "./AvailabilityLegend";
import { AvailabilityTiles } from "./AvailabilityTiles";
import { buildAvailabilityCsv, downloadCsv } from "./csv";
import { GroupMonthPanel } from "./GroupMonthPanel";
import { buildLayout } from "./grid-layout";
import { formatDataAsOf } from "./labels";
import {
  buildGrid,
  COLLAPSE_ABOVE,
  columnMonthsFor,
  showLastThree,
  tileCards,
  toViewRows,
} from "./model";
import { ResourceMonthPanel } from "./ResourceMonthPanel";
import {
  type AvailabilitySearch,
  type AvailabilityView,
  GROUP_OPTIONS,
  localToday,
  resolveView,
  type Selection,
  viewToSearch,
  windowForView,
  writeSavedView,
} from "./search-params";

interface AvailabilityReportProps {
  /** The validated URL search params. All view state lives here so a view is shareable. */
  search: AvailabilitySearch;
  onSearchChange: (next: AvailabilitySearch) => void;
}

/** True when a key press is going into a field, so a shortcut must stay out of its way. */
function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    target.isContentEditable ||
    target.getAttribute("role") === "combobox"
  );
}

const NO_OVERRIDES: Record<string, boolean> = {};

const SKELETON_ROWS = ["r1", "r2", "r3", "r4", "r5", "r6", "r7", "r8", "r9", "r10"];

function GridSkeleton() {
  return (
    <div className="flex flex-col gap-4" aria-busy="true">
      <span role="status" className="sr-only">
        Loading Availability
      </span>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(230px,1fr))] gap-3">
        {["free", "over", "rolling", "gaps"].map((id) => (
          <Skeleton key={id} className="h-[98px] rounded-lg" />
        ))}
      </div>
      <Skeleton className="h-6 w-2/3" />
      <div className="flex flex-col gap-1 rounded-lg border p-3">
        {SKELETON_ROWS.map((id) => (
          <Skeleton key={id} className="h-9 w-full" />
        ))}
      </div>
    </div>
  );
}

export function AvailabilityReport({ search, onSearchChange }: AvailabilityReportProps) {
  const view = useMemo(() => resolveView(search), [search]);
  const today = useMemo(() => localToday(), []);
  const { window: resolved, problem } = useMemo(() => windowForView(view, today), [view, today]);
  const firstMonth = resolved.months[0];
  const lastMonth = resolved.months[resolved.months.length - 1];

  const query = useAvailability({
    from: firstMonth ?? today,
    to: lastMonth ?? today,
    includeProposed: view.includeProposed,
  });
  const data = query.data;

  const { data: session } = useSession();
  const canOpenResource = canSeeResourcesPage(session?.user?.role);
  const searchStr = useRouterState({ select: (state) => state.location.searchStr });

  /** Change the view: merge a patch, then write the minimal search back to the URL. */
  const change = useCallback(
    (patch: Partial<AvailabilityView>) => {
      onSearchChange(viewToSearch({ ...view, ...patch }));
    },
    [view, onSearchChange],
  );

  // ── Keyboard: [ and ] step the window, T returns to Next 90 days, Esc closes the panel.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTyping(event.target)) return;
      if (event.key === "Escape" && view.selection !== null) {
        change({ selection: null });
        return;
      }
      const direction = event.key === "[" ? -1 : event.key === "]" ? 1 : 0;
      if (direction !== 0) {
        const stepped = stepWindow(resolved.months, direction);
        const from = stepped.months[0];
        const to = stepped.months[stepped.months.length - 1];
        if (from !== undefined && to !== undefined) {
          event.preventDefault();
          change({ preset: "custom", from, to, selection: null });
        }
        return;
      }
      if (event.key === "t" || event.key === "T") {
        event.preventDefault();
        change({ preset: "next90", from: null, to: null });
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [change, resolved.months, view.selection]);

  // ── Rows → grid model. Everything past the fetch runs in the browser. ──
  const rows = useMemo(() => (data ? toViewRows(data.resources) : []), [data]);
  const deferredQuery = useDeferredValue(view.q);
  const gridView = useMemo(() => ({ ...view, q: deferredQuery }), [view, deferredQuery]);

  // Columns depend on the payload and Show actual months only, so filtering never
  // rebuilds the layout and the memoised rows stay put while you type or click a tile.
  const columnMonths = useMemo(
    () => (data ? columnMonthsFor(data, view.showActual) : []),
    [data, view.showActual],
  );
  const lastThreeMonths = useMemo(
    () => (data && showLastThree(data, view.showActual) ? data.actualMonths : []),
    [data, view.showActual],
  );
  const grid = useMemo(
    () =>
      data
        ? buildGrid({ rows, view: gridView, payload: data, columnMonths, lastThreeMonths })
        : null,
    [data, rows, gridView, columnMonths, lastThreeMonths],
  );
  const layout = useMemo(
    () =>
      data
        ? buildLayout({
            columns: columnMonths,
            windowMonths: data.months,
            lastThreeMonths,
            today: data.today,
            planHorizon: data.planHorizon,
            compact: view.density === "compact",
          })
        : null,
    [data, columnMonths, lastThreeMonths, view.density],
  );

  // ── Groups load collapsed past 150 Resources; a search opens the groups it found. ──
  const [overrides, setOverrides] = useState<{ groupBy: GroupBy; open: Record<string, boolean> }>({
    groupBy: view.groupBy,
    open: {},
  });
  // A different grouping is a different set of groups, so earlier open/closed choices lapse.
  const openOverrides = overrides.groupBy === view.groupBy ? overrides.open : NO_OVERRIDES;
  const collapseByDefault = rows.length > COLLAPSE_ABOVE && view.q.trim() === "";
  const isOpen = useCallback(
    (groupId: string) => openOverrides[groupId] ?? !collapseByDefault,
    [openOverrides, collapseByDefault],
  );
  const onToggleGroup = useCallback(
    (groupId: string) =>
      setOverrides({
        groupBy: view.groupBy,
        open: { ...openOverrides, [groupId]: !isOpen(groupId) },
      }),
    [view.groupBy, openOverrides, isOpen],
  );
  const showGroups = view.groupBy !== "none";
  const anyOpen = grid?.groups.some((group) => isOpen(group.id)) ?? false;
  const onToggleAll = useCallback(() => {
    const open: Record<string, boolean> = {};
    for (const group of grid?.groups ?? []) open[group.id] = !anyOpen;
    setOverrides({ groupBy: view.groupBy, open });
  }, [grid, anyOpen, view.groupBy]);

  // ── Selection → panel ──
  const select = useCallback((selection: Selection | null) => change({ selection }), [change]);
  const onSelectResource = useCallback(
    (resourceId: string, month: string) => select({ kind: "resource", resourceId, month }),
    [select],
  );
  const onSelectGroup = useCallback(
    (groupKey: string | null, month: string) => select({ kind: "group", groupKey, month }),
    [select],
  );
  const closePanel = useCallback(() => select(null), [select]);

  const selection = view.selection;
  const selectedRow =
    selection?.kind === "resource"
      ? rows.find((row) => row.resourceId === selection.resourceId)
      : undefined;
  const selectedGroup =
    selection?.kind === "group"
      ? grid?.groups.find((group) => group.key === selection.groupKey)
      : undefined;

  const cards = useMemo(
    () => (data && grid ? tileCards(grid, data.planHorizon, data.months) : []),
    [data, grid],
  );
  const activeCard = cards.find((card) => card.id === view.tile);

  const onSaveView = () => {
    if (writeSavedView(searchStr)) {
      toast.success("View saved. This browser opens Availability with it.");
    } else {
      toast.error("Could not save the view: this browser's storage is unavailable.");
    }
  };

  const onExport = () => {
    if (!data || !grid) return;
    const exportRows = grid.groups.flatMap((group) => group.rows);
    downloadCsv(buildAvailabilityCsv(exportRows, data.months), `availability-${data.today}.csv`);
    toast.success(
      `Exported ${exportRows.length} ${exportRows.length === 1 ? "Resource" : "Resources"}`,
    );
  };

  const clearFilters = () => change({ q: "", tile: null, exceptionsOnly: false });
  const groupByLabel =
    GROUP_OPTIONS.find((option) => option.value === view.groupBy)?.label ?? "Home Role";

  const rangeLabel = `${resolved.label} · ${resolved.months.length} ${
    resolved.months.length === 1 ? "month" : "months"
  }`;

  return (
    <>
      <PageHeader
        eyebrow="Reports"
        title="Availability"
        description="Who can take work and who is over-utilized, month by month, in hours."
        actions={
          <>
            {data ? (
              <span className="mr-1.5 text-xs text-muted-foreground">
                Data as of {formatDataAsOf(data.dataAsOf)} from NOS
              </span>
            ) : null}
            <Button type="button" variant="outline" onClick={onSaveView}>
              Save view
            </Button>
            <Button type="button" variant="outline" onClick={onExport} disabled={!data}>
              Export CSV
            </Button>
          </>
        }
      />

      <AvailabilityControls
        view={view}
        onChange={change}
        windowLabel={resolved.label}
        rangeLabel={rangeLabel}
        windowEnds={
          firstMonth !== undefined && lastMonth !== undefined
            ? { from: firstMonth, to: lastMonth }
            : null
        }
        currentMonth={`${today.slice(0, 7)}-01`}
        windowProblem={problem}
        allOpen={anyOpen}
        canCollapse={showGroups && (grid?.groups.length ?? 0) > 0}
        onToggleAll={onToggleAll}
      />

      {query.isError && data === undefined ? (
        <ErrorState
          message={
            isNosNotConfiguredError(query.error)
              ? "NOS isn't configured for this environment, so Availability can't load. Ask an admin to connect it."
              : query.error instanceof Error
                ? query.error.message
                : "Availability could not be loaded."
          }
          onRetry={() => query.refetch()}
        />
      ) : query.isPending || data === undefined || grid === null || layout === null ? (
        <GridSkeleton />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={CalendarClock}
          title="No Resources are linked to NOS yet"
          description="Availability reads Available Hours and Guidance from NOS. Link a Resource to its NOS person and it appears here."
        />
      ) : (
        <>
          {query.isError ? (
            <p role="alert" className="text-xs text-destructive">
              Couldn&apos;t refresh. Showing the last data we have.{" "}
              <button
                type="button"
                onClick={() => query.refetch()}
                className="cursor-pointer font-semibold underline"
              >
                Try again
              </button>
            </p>
          ) : null}
          <AvailabilityTiles
            cards={cards}
            active={view.tile}
            onToggle={(id) => change({ tile: view.tile === id ? null : id })}
          />
          <AvailabilityLegend
            cutoffs={data.cutoffs}
            activeFilter={activeCard?.filterLabel ?? null}
            onClearFilter={() => change({ tile: null })}
          />
          <div className="flex flex-wrap items-start gap-4">
            <div
              className="flex min-w-0 flex-[999_1_640px] flex-col gap-2 transition-opacity data-[stale=true]:opacity-60"
              data-stale={query.isPlaceholderData}
            >
              <span className="text-xs text-muted-foreground">
                Hover a month for the numbers. Click it to see the Solutions behind it.
              </span>
              <AvailabilityGrid
                groups={grid.groups}
                layout={layout}
                showGroups={showGroups}
                isOpen={isOpen}
                onToggleGroup={onToggleGroup}
                selection={selection}
                onSelectResource={onSelectResource}
                onSelectGroup={onSelectGroup}
                visibleCount={grid.visibleCount}
                onClearFilters={clearFilters}
              />
              <span className="text-xs text-muted-foreground">
                Bands are the share of Available Hours Booked in the month. Proposed hours are
                hatched. [ and ] step the window, T returns to Next 90 days.
                {data.dataQuality.resourcesNotLinkedToNos > 0
                  ? ` ${data.dataQuality.resourcesNotLinkedToNos} active ${
                      data.dataQuality.resourcesNotLinkedToNos === 1
                        ? "Resource isn't"
                        : "Resources aren't"
                    } linked to NOS and ${data.dataQuality.resourcesNotLinkedToNos === 1 ? "isn't" : "aren't"} on this grid.`
                  : ""}
              </span>
            </div>
            {selection?.kind === "resource" && selectedRow !== undefined ? (
              <ResourceMonthPanel
                row={selectedRow}
                month={selection.month}
                allRows={rows}
                cutoffs={data.cutoffs}
                canOpenResource={canOpenResource}
                onClose={closePanel}
              />
            ) : null}
            {selection?.kind === "group" && selectedGroup !== undefined ? (
              <GroupMonthPanel
                group={selectedGroup}
                month={selection.month}
                groupByLabel={groupByLabel}
                cutoffs={data.cutoffs}
                onSelectResource={onSelectResource}
                onClose={closePanel}
              />
            ) : null}
          </div>
        </>
      )}
    </>
  );
}
